import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import { RadioGroup } from '../../../shared/components/RadioGroup';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import { listarUsuarios } from '../../usuarios/api';
import { ActionModal } from './ActionModal';
import {
  useAnexos,
  useAprovarMargem,
  useCancelarOportunidade,
  useConcorrentes,
  useGanharOportunidade,
  useMotivosPerda,
  usePerderOportunidade,
  useReativarOportunidade,
  useSuspenderOportunidade,
  useTransferirOportunidade,
} from '../hooks';
import type { Oportunidade, TipoEvidencia } from '../api';

interface BaseProps {
  opp: Oportunidade;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Options de tipoEvidencia (§Marcação como Ganha). Vocabulário
 * fechado espelha o CHECK constraint do backend.
 */
const TIPO_EVIDENCIA_OPTIONS: { value: TipoEvidencia; label: string }[] = [
  { value: 'proposta_assinada', label: 'Proposta assinada' },
  { value: 'contrato_assinado', label: 'Contrato assinado' },
  { value: 'pedido_compra', label: 'Pedido de compra' },
  { value: 'autorizacao_formal', label: 'Autorização formal' },
  { value: 'email_aceite', label: 'E-mail de aceite' },
  { value: 'outro', label: 'Outro' },
];

/**
 * Hook auxiliar: recarrega usuários para AutocompleteSelect (usado em
 * Transfer/Win). Cache curto — evita bater no /users em cada abertura.
 */
function useUsuarioOptions() {
  const { data } = useQuery({
    queryKey: ['picker', 'usuarios'],
    queryFn: () => listarUsuarios({ limit: 200 }),
    staleTime: 60 * 1000,
  });
  return useMemo(
    () =>
      (data?.data ?? []).map((u) => ({
        value: u.id,
        label: u.name,
        description: u.email,
      })),
    [data],
  );
}

// ---------------------------------------------------------------------
// Win
// ---------------------------------------------------------------------

export const WinOpportunityModal: React.FC<BaseProps> = ({ opp, isOpen, onClose, onSuccess }) => {
  const [valorFinal, setValorFinal] = useState(opp.valorEstimado ?? '');
  const [dataAceite, setDataAceite] = useState(new Date().toISOString().slice(0, 10));
  const [tipoEvidencia, setTipoEvidencia] = useState<TipoEvidencia | ''>('');
  const [anexoEvidenciaId, setAnexoEvidenciaId] = useState('');
  const [previsaoInicio, setPrevisaoInicio] = useState('');
  const [responsavelContinuidadeId, setResponsavelContinuidadeId] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const usuarioOptions = useUsuarioOptions();
  const mut = useGanharOportunidade(opp.id);
  const { data: anexos } = useAnexos(isOpen ? opp.id : undefined);
  const anexoOptions = useMemo(
    () =>
      (anexos ?? []).map((a) => ({
        value: a.id,
        label: a.filename,
        description: a.kind ?? undefined,
      })),
    [anexos],
  );

  const submit = async () => {
    setErr(null);
    if (!valorFinal || !dataAceite || !tipoEvidencia || !anexoEvidenciaId) return;
    try {
      await mut.mutateAsync({
        valorFinal,
        dataAceite,
        tipoEvidencia,
        anexoEvidenciaId,
        previsaoInicio: previsaoInicio || undefined,
        responsavelContinuidadeId: responsavelContinuidadeId || undefined,
      });
      onSuccess?.();
      onClose();
    } catch (e) {
      setErr(getFriendlyErrorMessage(e, 'Falha ao marcar como ganha.'));
    }
  };

  const canSubmit = !!valorFinal && !!dataAceite && !!tipoEvidencia && !!anexoEvidenciaId;

  return (
    <ActionModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={submit}
      title="Marcar como Ganha"
      description="Registra o desfecho de ganho, atualiza probabilidade para 100%, e (se aplicável) promove o cliente Prospect/Em Cadastro para Ativo."
      submitLabel="Confirmar ganho"
      submitVariant="success"
      isSubmitting={mut.isPending}
      submitError={err}
      canSubmit={canSubmit}
    >
      <div className="space-y-3">
        <TextInput
          label="Valor final (BRL)"
          type="number"
          value={valorFinal}
          onChange={(e) => setValorFinal(e.target.value)}
        />
        <TextInput
          label="Data de aceite"
          type="date"
          value={dataAceite}
          onChange={(e) => setDataAceite(e.target.value)}
        />
        <AutocompleteSelect
          label="Tipo de evidência"
          options={TIPO_EVIDENCIA_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          value={tipoEvidencia}
          onChange={(v) => setTipoEvidencia(v as TipoEvidencia | '')}
        />
        <AutocompleteSelect
          label="Anexo de evidência"
          options={anexoOptions}
          value={anexoEvidenciaId}
          onChange={setAnexoEvidenciaId}
        />
        {anexoOptions.length === 0 && (
          <p className="text-xs text-gray-500">
            Nenhum anexo vinculado ainda. Envie um pela seção "Anexos" no detalhe
            antes de marcar como Ganha.
          </p>
        )}
        <TextInput
          label="Previsão de início (opcional)"
          type="date"
          value={previsaoInicio}
          onChange={(e) => setPrevisaoInicio(e.target.value)}
        />
        <AutocompleteSelect
          label="Responsável pela continuidade (opcional)"
          options={usuarioOptions}
          value={responsavelContinuidadeId}
          onChange={setResponsavelContinuidadeId}
        />
      </div>
    </ActionModal>
  );
};

// ---------------------------------------------------------------------
// Lose
// ---------------------------------------------------------------------

export const LoseOpportunityModal: React.FC<BaseProps> = ({ opp, isOpen, onClose, onSuccess }) => {
  const [motivoId, setMotivoId] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [concorrenteVencedorId, setConcorrenteVencedorId] = useState('');
  const [valorFinalConhecido, setValorFinalConhecido] = useState('');
  const [possibilidadeRetomada, setPossibilidadeRetomada] = useState<'sim' | 'nao'>('nao');
  const [licoesAprendidas, setLicoesAprendidas] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const { data: motivos } = useMotivosPerda();
  const { data: concorrentes } = useConcorrentes(opp.id);
  const mut = usePerderOportunidade(opp.id);

  const motivoOptions = useMemo(
    () => (motivos ?? []).map((m) => ({ value: m.id, label: m.label })),
    [motivos],
  );

  // Só concorrentes já vinculados à oportunidade podem ser vencedores.
  // O backend valida — se o usuário conseguir burlar, cai em 422.
  const concorrenteOptions = useMemo(
    () =>
      (concorrentes ?? []).map((c) => ({
        value: c.competitorId,
        label: c.competitorName ?? 'Concorrente sem nome',
      })),
    [concorrentes],
  );

  const submit = async () => {
    setErr(null);
    if (!motivoId || justificativa.trim().length < 20) return;
    try {
      await mut.mutateAsync({
        motivoId,
        justificativa: justificativa.trim(),
        concorrenteVencedorId: concorrenteVencedorId || undefined,
        valorFinalConhecido: valorFinalConhecido || undefined,
        possibilidadeRetomada: possibilidadeRetomada === 'sim',
        licoesAprendidas: licoesAprendidas.trim() || undefined,
      });
      onSuccess?.();
      onClose();
    } catch (e) {
      setErr(getFriendlyErrorMessage(e, 'Falha ao marcar como perdida.'));
    }
  };

  const canSubmit = !!motivoId && justificativa.trim().length >= 20;

  return (
    <ActionModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={submit}
      title="Marcar como Perdida"
      description="Registra o desfecho de perda. Justificativa mínima de 20 caracteres para consistência da análise de conversão."
      submitLabel="Confirmar perda"
      submitVariant="danger"
      isSubmitting={mut.isPending}
      submitError={err}
      canSubmit={canSubmit}
    >
      <div className="space-y-3">
        <AutocompleteSelect
          label="Motivo da perda"
          options={motivoOptions}
          value={motivoId}
          onChange={setMotivoId}
        />
        <Textarea
          label="Justificativa (mín. 20 caracteres)"
          rows={3}
          value={justificativa}
          onChange={(e) => setJustificativa(e.target.value)}
          showCharCount
          minLength={20}
        />
        {concorrenteOptions.length > 0 ? (
          <div>
            <AutocompleteSelect
              label="Concorrente vencedor (opcional)"
              options={concorrenteOptions}
              value={concorrenteVencedorId}
              onChange={setConcorrenteVencedorId}
            />
            {concorrenteVencedorId && (
              <button
                type="button"
                onClick={() => setConcorrenteVencedorId('')}
                className="mt-1 text-xs text-blue-600 hover:underline"
              >
                Limpar seleção
              </button>
            )}
          </div>
        ) : (
          <p className="rounded-md border border-gray-200 bg-gray-50 p-2 text-xs text-gray-600">
            Nenhum concorrente vinculado a esta oportunidade. Se quiser registrar
            o vencedor, vincule-o primeiro na seção "Concorrentes" do detalhe.
          </p>
        )}
        <TextInput
          label="Valor final conhecido (BRL, opcional)"
          type="number"
          value={valorFinalConhecido}
          onChange={(e) => setValorFinalConhecido(e.target.value)}
        />
        <RadioGroup
          legend="Possibilidade de retomada"
          name="retomada"
          options={[
            { value: 'sim', label: 'Sim' },
            { value: 'nao', label: 'Não' },
          ]}
          value={possibilidadeRetomada}
          onChange={(v) => setPossibilidadeRetomada(v as 'sim' | 'nao')}
        />
        <Textarea
          label="Lições aprendidas (opcional)"
          rows={2}
          value={licoesAprendidas}
          onChange={(e) => setLicoesAprendidas(e.target.value)}
        />
      </div>
    </ActionModal>
  );
};

// ---------------------------------------------------------------------
// Suspend
// ---------------------------------------------------------------------

export const SuspendOpportunityModal: React.FC<BaseProps> = ({ opp, isOpen, onClose, onSuccess }) => {
  const [motivo, setMotivo] = useState('');
  const [dataSuspensao, setDataSuspensao] = useState(new Date().toISOString().slice(0, 10));
  const [previsaoRetomada, setPrevisaoRetomada] = useState('');
  const [responsavelAcompanhamentoId, setResponsavelAcompanhamentoId] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const usuarioOptions = useUsuarioOptions();
  const mut = useSuspenderOportunidade(opp.id);

  const submit = async () => {
    setErr(null);
    if (motivo.trim().length < 10 || !previsaoRetomada) return;
    try {
      await mut.mutateAsync({
        motivo: motivo.trim(),
        dataSuspensao: dataSuspensao || undefined,
        previsaoRetomada,
        responsavelAcompanhamentoId: responsavelAcompanhamentoId || undefined,
      });
      onSuccess?.();
      onClose();
    } catch (e) {
      setErr(getFriendlyErrorMessage(e, 'Falha ao suspender oportunidade.'));
    }
  };

  return (
    <ActionModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={submit}
      title="Suspender oportunidade"
      description="A oportunidade sai do pipeline ativo até a data prevista de retomada. A etapa anterior fica registrada para restaurar na reativação."
      submitLabel="Suspender"
      submitVariant="warning"
      isSubmitting={mut.isPending}
      submitError={err}
      canSubmit={motivo.trim().length >= 10 && !!previsaoRetomada}
    >
      <div className="space-y-3">
        <Textarea
          label="Motivo da suspensão (mín. 10 caracteres)"
          rows={2}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          showCharCount
          minLength={10}
        />
        <TextInput
          label="Data de suspensão"
          type="date"
          value={dataSuspensao}
          onChange={(e) => setDataSuspensao(e.target.value)}
        />
        <TextInput
          label="Previsão de retomada"
          type="date"
          value={previsaoRetomada}
          onChange={(e) => setPrevisaoRetomada(e.target.value)}
        />
        <AutocompleteSelect
          label="Responsável pelo acompanhamento (opcional)"
          options={usuarioOptions}
          value={responsavelAcompanhamentoId}
          onChange={setResponsavelAcompanhamentoId}
        />
      </div>
    </ActionModal>
  );
};

// ---------------------------------------------------------------------
// Reactivate
// ---------------------------------------------------------------------

export const ReactivateOpportunityModal: React.FC<BaseProps> = ({ opp, isOpen, onClose, onSuccess }) => {
  const [newExpectedClosingDate, setNewExpectedClosingDate] = useState('');
  const [nextActionDate, setNextActionDate] = useState('');
  const [nextActionDescription, setNextActionDescription] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const mut = useReativarOportunidade(opp.id);

  const submit = async () => {
    setErr(null);
    if (!newExpectedClosingDate || !nextActionDate || !nextActionDescription.trim()) return;
    try {
      await mut.mutateAsync({
        newExpectedClosingDate,
        nextActionDate: new Date(nextActionDate).toISOString(),
        nextActionDescription: nextActionDescription.trim(),
      });
      onSuccess?.();
      onClose();
    } catch (e) {
      setErr(getFriendlyErrorMessage(e, 'Falha ao reativar oportunidade.'));
    }
  };

  return (
    <ActionModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={submit}
      title="Reativar oportunidade"
      description="A oportunidade retorna à etapa anterior à suspensão. Nova data prevista e próxima ação são obrigatórias para manter o pipeline saudável."
      submitLabel="Reativar"
      submitVariant="primary"
      isSubmitting={mut.isPending}
      submitError={err}
      canSubmit={
        !!newExpectedClosingDate && !!nextActionDate && !!nextActionDescription.trim()
      }
    >
      <div className="space-y-3">
        <TextInput
          label="Nova data prevista de fechamento"
          type="date"
          value={newExpectedClosingDate}
          onChange={(e) => setNewExpectedClosingDate(e.target.value)}
        />
        <TextInput
          label="Nova próxima ação — data/hora"
          type="datetime-local"
          value={nextActionDate}
          onChange={(e) => setNextActionDate(e.target.value)}
        />
        <Textarea
          label="Descrição da próxima ação"
          rows={2}
          value={nextActionDescription}
          onChange={(e) => setNextActionDescription(e.target.value)}
        />
      </div>
    </ActionModal>
  );
};

// ---------------------------------------------------------------------
// Cancel
// ---------------------------------------------------------------------

export const CancelOpportunityModal: React.FC<BaseProps> = ({ opp, isOpen, onClose, onSuccess }) => {
  const [justificativa, setJustificativa] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const mut = useCancelarOportunidade(opp.id);

  const submit = async () => {
    setErr(null);
    if (justificativa.trim().length < 20) return;
    try {
      await mut.mutateAsync({ justificativa: justificativa.trim() });
      onSuccess?.();
      onClose();
    } catch (e) {
      setErr(getFriendlyErrorMessage(e, 'Falha ao cancelar oportunidade.'));
    }
  };

  return (
    <ActionModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={submit}
      title="Cancelar oportunidade"
      description="Use quando a oportunidade foi criada indevidamente ou não pode prosseguir. É o desfecho recomendado em vez de exclusão física."
      submitLabel="Cancelar oportunidade"
      submitVariant="danger"
      isSubmitting={mut.isPending}
      submitError={err}
      canSubmit={justificativa.trim().length >= 20}
    >
      <Textarea
        label="Justificativa (mín. 20 caracteres)"
        rows={3}
        value={justificativa}
        onChange={(e) => setJustificativa(e.target.value)}
        showCharCount
        minLength={20}
      />
    </ActionModal>
  );
};

// ---------------------------------------------------------------------
// Transfer
// ---------------------------------------------------------------------

export const TransferResponsibleModal: React.FC<BaseProps> = ({ opp, isOpen, onClose, onSuccess }) => {
  const [newResponsavelId, setNewResponsavelId] = useState('');
  const [motivo, setMotivo] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const usuarioOptions = useUsuarioOptions();
  const mut = useTransferirOportunidade(opp.id);

  const submit = async () => {
    setErr(null);
    if (!newResponsavelId || motivo.trim().length < 10) return;
    try {
      await mut.mutateAsync({ newResponsavelId, motivo: motivo.trim() });
      onSuccess?.();
      onClose();
    } catch (e) {
      setErr(getFriendlyErrorMessage(e, 'Falha ao transferir responsável.'));
    }
  };

  return (
    <ActionModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={submit}
      title="Transferir responsável"
      description="Troca o responsável comercial. Interações e demais registros já criados mantêm o autor original."
      submitLabel="Transferir"
      submitVariant="primary"
      isSubmitting={mut.isPending}
      submitError={err}
      canSubmit={!!newResponsavelId && motivo.trim().length >= 10}
    >
      <div className="space-y-3">
        <AutocompleteSelect
          label="Novo responsável"
          options={usuarioOptions.filter((u) => u.value !== opp.responsavelId)}
          value={newResponsavelId}
          onChange={setNewResponsavelId}
        />
        <Textarea
          label="Motivo (mín. 10 caracteres)"
          rows={2}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          showCharCount
          minLength={10}
        />
      </div>
    </ActionModal>
  );
};

// ---------------------------------------------------------------------
// Approve margin
// ---------------------------------------------------------------------

export const MarginApprovalModal: React.FC<BaseProps> = ({ opp, isOpen, onClose, onSuccess }) => {
  const [justification, setJustification] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const mut = useAprovarMargem(opp.id);

  const submit = async () => {
    setErr(null);
    if (justification.trim().length < 10) return;
    try {
      await mut.mutateAsync({ justification: justification.trim() });
      onSuccess?.();
      onClose();
    } catch (e) {
      setErr(getFriendlyErrorMessage(e, 'Falha ao aprovar margem.'));
    }
  };

  return (
    <ActionModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={submit}
      title="Aprovar margem"
      description="Libera avanço da oportunidade para Proposta mesmo com margem abaixo do limite parametrizável. A aprovação é invalidada automaticamente ao editar valor ou custo."
      submitLabel="Aprovar"
      submitVariant="warning"
      isSubmitting={mut.isPending}
      submitError={err}
      canSubmit={justification.trim().length >= 10}
    >
      <div className="space-y-2 text-sm">
        <div className="rounded border border-amber-200 bg-amber-50 p-3 text-amber-900">
          <div>
            Valor estimado atual: <span className="font-mono">{opp.valorEstimado ?? '—'}</span>
          </div>
          <div>
            Custo estimado atual: <span className="font-mono">{opp.custoEstimado ?? '—'}</span>
          </div>
          <div>
            Margem percentual: <span className="font-mono">{opp.margemPercentual ?? '—'}%</span>
          </div>
        </div>
        <Textarea
          label="Justificativa (mín. 10 caracteres)"
          rows={3}
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
          showCharCount
          minLength={10}
        />
      </div>
    </ActionModal>
  );
};
